import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Clock, AlertTriangle, ExternalLink, BookOpen, ArrowRight, Calendar, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";

const MailboxHistory = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": { "@type": "WebPage", "@id": "https://csw.live/mailbox/history/" },
    "headline": "Mailbox: The Email App That Promised Inbox Zero",
    "description": "The history of Mailbox, the innovative email app that Dropbox acquired for $100 million and discontinued in 2016.",
    "author": { "@type": "Organization", "name": "CSW Team", "url": "https://csw.live" },
    "publisher": { "@type": "Organization", "name": "CSW - Chill Study Work", "url": "https://csw.live" },
    "datePublished": "2025-01-11",
    "dateModified": "2025-01-11"
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Mailbox History - The Email App That Promised Inbox Zero"
        description="A retrospective on Mailbox - the innovative email app that Dropbox acquired for $100 million and discontinued in 2016."
        keywords="mailbox history, mailbox app shutdown, dropbox acquisition, inbox zero, email app history"
        canonicalUrl="/mailbox/history/"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Header />
      
      <main className="pt-20">
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
          <div className="container mx-auto px-4 relative z-10">
            <nav className="mb-8">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li>/</li>
                <li><Link to="/mailbox/" className="hover:text-primary">Mailbox</Link></li>
                <li>/</li>
                <li className="text-foreground">History</li>
              </ol>
            </nav>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Calendar className="w-4 h-4" />
                A Retrospective • 2013-2016
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                The Mailbox Story
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8">
                The email app that reimagined inbox management with swipe gestures and snooze features.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
                <CardContent className="p-8">
                  <p className="text-lg text-muted-foreground">
                    Mailbox was created by Orchestra, founded by Gentry Underwood and Scott Cannon. It launched in early 2013 and famously had a <span className="text-foreground font-semibold">waiting list of hundreds of thousands</span>—users could watch their position in line via a live counter.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl font-bold">Revolutionary Features</h2>
              </div>
              
              <div className="space-y-6">
                <p className="text-muted-foreground text-lg">
                  Mailbox's claim to fame was introducing the world to <span className="text-foreground font-medium">"email triage"</span> on mobile with intuitive swipe gestures.
                </p>

                <Card className="border-border/50">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-4">Swipe Gestures</h3>
                    <ul className="grid md:grid-cols-2 gap-3 text-muted-foreground">
                      <li className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-primary" /> Short swipe right = Archive</li>
                      <li className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-primary" /> Long swipe right = Delete</li>
                      <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> Short swipe left = Snooze</li>
                      <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /> Long swipe left = Move to list</li>
                    </ul>
                  </CardContent>
                </Card>

                <p className="text-muted-foreground text-lg">
                  Mailbox was one of the first apps to let you <span className="text-foreground font-medium">snooze emails</span>—dismiss an email and have it come back later. This helped users focus on what mattered now. The concept of treating your inbox like a to-do list resonated deeply.
                </p>

                <p className="text-muted-foreground text-lg">
                  Just one month after launch, in <span className="text-foreground font-semibold">March 2013, Dropbox acquired Mailbox</span> for reportedly around $100 million.
                </p>

                <a href="https://www.wired.com/2015/12/dropbox-kills-carousel-and-mailbox-as-it-turns-toward-businesses/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm">
                  <ExternalLink className="w-4 h-4" />
                  Wired: Dropbox Kills Mailbox
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-destructive/10">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="text-3xl font-bold">The Shutdown</h2>
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  On <span className="text-foreground font-semibold">December 7, 2015</span>, Dropbox announced it would shut down Mailbox at the end of February 2016. The app was officially discontinued on <span className="text-foreground font-semibold">February 26, 2016</span>.
                </p>

                <Card className="border-destructive/30 bg-destructive/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl mb-4">Why Dropbox Killed Mailbox</p>
                    <p className="text-muted-foreground">
                      In a candid blog post, Dropbox's founders admitted that while Mailbox was a worthwhile experiment, they needed to focus on their core mission of collaboration. They noted <span className="italic">"there's only so much an email app can do to fundamentally fix email."</span>
                    </p>
                  </CardContent>
                </Card>

                <p>
                  Mailbox wasn't gaining new users—by late 2015 it was around #233 in productivity apps. Competing email apps had emerged, many copying Mailbox's ideas (Gmail's Inbox, Spark). The innovation had been absorbed into the industry, making the original product unsustainable.
                </p>

                <div className="flex flex-wrap gap-3">
                  <a href="https://techcrunch.com/2015/12/07/failing-to-find-users-dropbox-will-shut-down-mailbox-in-february-2016-and-carousel-in-march/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm">
                    <ExternalLink className="w-4 h-4" />
                    TechCrunch: Mailbox Shutdown
                  </a>
                  <a href="https://blog.dropbox.com/topics/company/saying-goodbye-to-carousel-and-mailbox" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm">
                    <ExternalLink className="w-4 h-4" />
                    Dropbox Blog: Saying Goodbye
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">Looking for Productivity Tools?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              CSW helps you stay productive with task management, focus timers, and virtual accountability sessions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try CSW Free <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/mailbox/">View Mailbox Alternative</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MailboxHistory;
